/**
 * Custom hook for tracking time spent on an interface
 * Handles visibility changes, tab switches, and periodic flushing
 */
import { useEffect, useRef, useCallback } from 'react';
import { recordTimeSpent, sendTimeSpentBeacon } from '../../api/usage/usageApi';

/**
 * Hook to track time spent on an interface
 * @param {Object} options - Configuration options
 * @param {string} options.interfaceName - Name of the interface (e.g., 'chat')
 * @param {number} [options.flushIntervalMs=15000] - How often to flush time to server (default 15s)
 * @param {boolean} [options.enabled=true] - Whether tracking is enabled
 * @returns {Object} Tracking state and controls
 */
export const useTimeSpentTracker = ({
  interfaceName,
  flushIntervalMs = 15000, // 15 seconds
  enabled = true
}) => {
  const startTimeRef = useRef(null);
  const accumulatedSecondsRef = useRef(0);
  const isTrackingRef = useRef(false);
  const flushIntervalRef = useRef(null);
  const tabLockKeyRef = useRef(`time_tracker_lock_${interfaceName}`);
  const hasTabLockRef = useRef(false);

  /**
   * Try to acquire tab lock (simple localStorage-based lock)
   * Only one tab should track time at a time
   */
  const acquireTabLock = useCallback(() => {
    try {
      const lock = localStorage.getItem(tabLockKeyRef.current);
      const now = Date.now();
      
      // If no lock exists or lock is stale (>30s old), acquire it
      if (!lock || (now - parseInt(lock, 10)) > 30000) {
        localStorage.setItem(tabLockKeyRef.current, now.toString());
        hasTabLockRef.current = true;
        return true;
      }
      
      hasTabLockRef.current = false;
      return false;
    } catch (error) {
      // If localStorage not available, assume we have lock
      console.warn('localStorage not available for tab lock:', error);
      hasTabLockRef.current = true;
      return true;
    }
  }, []);

  /**
   * Release tab lock
   */
  const releaseTabLock = useCallback(() => {
    try {
      if (hasTabLockRef.current) {
        localStorage.removeItem(tabLockKeyRef.current);
        hasTabLockRef.current = false;
      }
    } catch (error) {
      console.warn('Error releasing tab lock:', error);
    }
  }, []);

  /**
   * Start tracking time
   */
  const startTracking = useCallback(() => {
    if (!enabled || isTrackingRef.current) return;
    
    // Only track if we have the tab lock
    if (!acquireTabLock()) {
      return;
    }

    startTimeRef.current = Date.now();
    isTrackingRef.current = true;
  }, [enabled, interfaceName, acquireTabLock]);

  /**
   * Pause tracking (accumulate current session)
   */
  const pauseTracking = useCallback(() => {
    if (!isTrackingRef.current || !startTimeRef.current) return;

    const now = Date.now();
    const elapsedSeconds = Math.floor((now - startTimeRef.current) / 1000);
    
    if (elapsedSeconds > 0) {
      accumulatedSecondsRef.current += elapsedSeconds;
    }

    startTimeRef.current = null;
    isTrackingRef.current = false;
  }, []);

  /**
   * Flush accumulated time to server
   */
  const flushTime = useCallback(async (useBeacon = false) => {
    // Pause to accumulate any current tracking
    pauseTracking();

    const secondsToFlush = accumulatedSecondsRef.current;
    
    // Reset accumulated seconds immediately to prevent double-sending
    accumulatedSecondsRef.current = 0;

    if (secondsToFlush <= 0) {
      return;
    }

    const payload = {
      interface: interfaceName,
      seconds: secondsToFlush,
      endedAt: new Date().toISOString()
    };

    try {
      if (useBeacon) {
        // Use beacon for unload events
        sendTimeSpentBeacon(payload);
      } else {
        // Regular API call
        await recordTimeSpent(payload);
      }
    } catch (error) {
      console.error(`[TimeTracker] Failed to flush time for ${interfaceName}:`, error);
      // Restore accumulated seconds on error (optional)
      accumulatedSecondsRef.current += secondsToFlush;
    }
  }, [interfaceName, pauseTracking]);

  /**
   * Handle visibility change (tab switch, minimize)
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Page is hidden - pause tracking
      pauseTracking();
    } else {
      // Page is visible - resume tracking
      startTracking();
    }
  }, [pauseTracking, startTracking]);

  /**
   * Handle page focus/blur
   */
  const handleFocus = useCallback(() => {
    startTracking();
  }, [startTracking]);

  const handleBlur = useCallback(() => {
    pauseTracking();
  }, [pauseTracking]);

  /**
   * Handle page unload/beforeunload
   */
  const handleUnload = useCallback(() => {
    // Flush with beacon
    flushTime(true);
    releaseTabLock();
  }, [flushTime, releaseTabLock]);

  /**
   * Setup tracking on mount
   */
  useEffect(() => {
    if (!enabled) return;

    // Start tracking if page is visible
    if (!document.hidden) {
      startTracking();
    }

    // Setup periodic flush
    flushIntervalRef.current = setInterval(() => {
      flushTime(false);
      // Restart tracking after flush if page is still visible
      if (!document.hidden) {
        startTracking();
      }
    }, flushIntervalMs);

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('unload', handleUnload);

    // Cleanup on unmount
    return () => {
      // Clear interval
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
      }

      // Remove listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('unload', handleUnload);

      // Final flush
      flushTime(true);
      releaseTabLock();
    };
  }, [
    enabled,
    flushIntervalMs,
    startTracking,
    flushTime,
    handleVisibilityChange,
    handleFocus,
    handleBlur,
    handleUnload,
    releaseTabLock
  ]);

  return {
    isTracking: isTrackingRef.current,
    flushNow: () => flushTime(false)
  };
};
